import { apiClient } from './client';
import type {
  Site,
  SiteWithScore,
  PaginatedResponse,
  AnalyticalResult,
  MonitoringWell,
  Receptor,
} from '@/types';

export interface SiteFilters {
  status?: string;
  state?: string;
  threat_tier?: number;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface CreateSiteRequest {
  // Required (matches backend SiteCreate at src/api/routes/sites.py:46)
  site_identifier: string;
  name: string;
  // Optional — most can be filled in later via SiteDetail edit
  status?: string;
  state?: string;
  county?: string;
  city?: string;
  address?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  aquifer_type?: string;
  primary_contaminant?: string;
  primary_contaminant_other?: string;
  regulatory_program?: string;
  case_number?: string;
  depth_to_groundwater_ft?: number;
  plume_area_sqft?: number;
  estimated_mass_lbs?: number;
  discovery_date?: string;
}

export interface UploadAnalyticalRequest {
  results: Array<{
    sample_date: string;
    parameter: string;
    result_value?: number;
    unit?: string;
    is_non_detect?: boolean;
    well_id?: string;
  }>;
}

export const sitesApi = {
  list: async (filters?: SiteFilters): Promise<PaginatedResponse<SiteWithScore>> => {
    const response = await apiClient.get<PaginatedResponse<SiteWithScore>>('/sites/', {
      params: filters,
    });
    return response.data;
  },

  get: async (id: string): Promise<Site> => {
    const response = await apiClient.get<Site>(`/sites/${id}`);
    return response.data;
  },

  create: async (data: CreateSiteRequest): Promise<Site> => {
    const response = await apiClient.post<Site>('/sites/', data);
    return response.data;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<Site> => {
    const response = await apiClient.patch<Site>(`/sites/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/sites/${id}`);
  },

  // Analytical data
  getAnalyticalData: async (
    siteId: string,
    params?: { parameter?: string; start_date?: string; end_date?: string }
  ): Promise<AnalyticalResult[]> => {
    const response = await apiClient.get<AnalyticalResult[]>(
      `/sites/${siteId}/analytical`,
      { params }
    );
    return response.data;
  },

  uploadAnalyticalData: async (
    siteId: string,
    data: UploadAnalyticalRequest
  ): Promise<{ uploaded_count: number }> => {
    const response = await apiClient.post(`/sites/${siteId}/analytical`, data);
    return response.data;
  },

  // Wells
  getWells: async (siteId: string): Promise<MonitoringWell[]> => {
    const response = await apiClient.get(`/sites/${siteId}/wells`);
    const data = response.data;
    return Array.isArray(data) ? data : data.wells ?? [];
  },

  createWell: async (siteId: string, payload: CreateWellRequest): Promise<CreatedWellResponse> => {
    const response = await apiClient.post<CreatedWellResponse>(`/sites/${siteId}/wells`, payload);
    return response.data;
  },

  // Receptors
  getReceptors: async (siteId: string): Promise<Receptor[]> => {
    const response = await apiClient.get(`/sites/${siteId}/receptors`);
    const data = response.data;
    return Array.isArray(data) ? data : data.receptors ?? [];
  },

  createReceptor: async (siteId: string, data: CreateReceptorRequest): Promise<{ receptor_id: string }> => {
    const response = await apiClient.post(`/sites/${siteId}/receptors`, data);
    return response.data;
  },

  // GIS import
  uploadGIS: async (siteId: string, files: File[]): Promise<GISPreviewResponse> => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await apiClient.post<GISPreviewResponse>(
      `/sites/${siteId}/wells/upload-gis`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  confirmGIS: async (
    siteId: string,
    previewId: string,
    excludedWells: string[] = [],
    coordinateEdits: Record<string, { latitude: number; longitude: number }> = {}
  ): Promise<GISImportResponse> => {
    const response = await apiClient.post<GISImportResponse>(
      `/sites/${siteId}/wells/confirm-gis`,
      { preview_id: previewId, excluded_wells: excludedWells, coordinate_edits: coordinateEdits }
    );
    return response.data;
  },

  // XLSX bulk-import (one row per well) — preview/confirm shape matches GIS
  uploadWellsXLSX: async (siteId: string, file: File): Promise<GISPreviewResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<GISPreviewResponse>(
      `/sites/${siteId}/wells/upload-xlsx`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  confirmWellsXLSX: async (
    siteId: string,
    previewId: string,
    excludedWells: string[] = [],
    coordinateEdits: Record<string, { latitude: number; longitude: number }> = {}
  ): Promise<GISImportResponse> => {
    const response = await apiClient.post<GISImportResponse>(
      `/sites/${siteId}/wells/confirm-xlsx`,
      { preview_id: previewId, excluded_wells: excludedWells, coordinate_edits: coordinateEdits }
    );
    return response.data;
  },
};

// Manual single-well add — mirrors backend WellCreate at
// src/api/routes/sites.py POST /sites/{site_id}/wells.
export interface CreateWellInitialReading {
  measurement_date: string;  // YYYY-MM-DD
  depth_to_water_ft?: number;
  depth_to_product_ft?: number;
  flag_code?: string;  // validated against the tenant's flag_codes table at write time
  notes?: string;
}

export interface CreateWellRequest {
  well_name: string;
  well_type?: 'monitoring' | 'recovery' | 'mdpe' | 'soil_boring';
  latitude: number;
  longitude: number;
  total_depth_ft?: number;
  screen_top_ft?: number;
  screen_bottom_ft?: number;
  casing_diameter_in?: number;
  casing_elevation_ft?: number;
  ground_elevation_ft?: number;
  casing_material?: string;
  install_date?: string;  // YYYY-MM-DD
  status?: 'active' | 'inactive' | 'abandoned';
  initial_reading?: CreateWellInitialReading;
}

export interface CreatedWellResponse {
  id: string;
  well_id: string;
  well_name: string;
  well_type: string;
  latitude: number;
  longitude: number;
  total_depth_ft: number | null;
  screen_top_ft: number | null;
  screen_bottom_ft: number | null;
  casing_diameter_in: number | null;
  casing_elevation_ft: number | null;
  ground_elevation_ft: number | null;
  casing_material: string | null;
  install_date: string | null;
  status: string;
  is_active: boolean;
  coordinate_source: string | null;
  source_system: string | null;
  gauging_id: string | null;
}

// Request/response types for GIS import
export interface CreateReceptorRequest {
  receptor_type: string;
  name?: string;
  latitude: number;
  longitude: number;
  distance_ft: number;
  direction_degrees?: number;
  is_downgradient?: boolean;
  population_served?: number;
  sensitivity_score?: number;
}

export interface GISWellPreview {
  well_name: string;
  latitude: number;
  longitude: number;
  station_number?: number;
  match_status: 'matched' | 'new' | 'conflict';
  matched_well_id?: string;
  existing_lat?: number;
  existing_lon?: number;
  existing_source?: string;
  will_update: boolean;
  properties: Record<string, unknown>;
}

export interface GISPreviewResponse {
  preview_id: string;
  site_id: string;
  filename: string;
  crs?: string;
  total_features: number;
  matched: number;
  new_wells: number;
  conflicts: number;
  wells: GISWellPreview[];
}

export interface GISImportResponse {
  run_id: string;
  inserted: number;
  updated: number;
  skipped_priority: number;
  unmatched: number;
  coordinate_conflicts: number;
}
